import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { RefreshCw, Save, X, DollarSign, Hash } from 'lucide-react';
import AgentLayout from '../components/AgentLayout';
import api from '../services/api';

const AdminTradosAgentSettings = () => {
  const [agentSettings, setAgentSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsDirty, setSettingsDirty] = useState(false);

  const fetchSettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const res = await api.get('/admin/trados-agent/settings');
      setAgentSettings(res.data);
      setSettingsDirty(false);
    } catch (err) { toast.error('Failed to load settings'); }
    finally { setSettingsLoading(false); }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const updateSettingsField = (field, value) => {
    setAgentSettings(prev => ({ ...prev, [field]: value }));
    setSettingsDirty(true);
  };

  const updateBudgetField = (field, value) => {
    setAgentSettings(prev => ({
      ...prev,
      budget: { ...prev.budget, [field]: value }
    }));
    setSettingsDirty(true);
  };

  const saveSettings = async () => {
    if (!agentSettings) return;
    try {
      await api.put('/admin/trados-agent/settings', {
        model_id: agentSettings.model_id,
        temperature: agentSettings.temperature,
        max_tokens: agentSettings.max_tokens,
        budget_mode: agentSettings.budget?.mode,
        monthly_budget: agentSettings.budget?.monthly_budget,
      });
      toast.success('Settings saved');
      setSettingsDirty(false);
    } catch (err) { toast.error('Failed to save settings'); }
  };

  return (
    <AgentLayout>
      <div className="space-y-6">
        {settingsLoading || !agentSettings ? (
          <div className="bg-white rounded-xl border border-gray-200 flex items-center justify-center py-12 text-gray-400">
            <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading settings...
          </div>
        ) : (
          <>
            {/* Model Configuration */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Model Configuration</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">AI Model</label>
                  <select value={agentSettings.model_id || ''}
                    onChange={(e) => updateSettingsField('model_id', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500">
                    <option value="">Select a model...</option>
                    {(agentSettings.available_models || []).map(m => (
                      <option key={m.id} value={m.id}>{m.name} ({m.provider})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Temperature: {agentSettings.temperature}
                  </label>
                  <input type="range" min="0" max="2" step="0.1" value={agentSettings.temperature}
                    onChange={(e) => updateSettingsField('temperature', parseFloat(e.target.value))}
                    className="w-full accent-indigo-600" />
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>Precise (0)</span><span>Creative (2)</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Tokens: {agentSettings.max_tokens?.toLocaleString()}
                  </label>
                  <input type="range" min="256" max="32768" step="256" value={agentSettings.max_tokens}
                    onChange={(e) => updateSettingsField('max_tokens', parseInt(e.target.value))}
                    className="w-full accent-indigo-600" />
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>256</span><span>32,768</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Budget Management */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Budget Management</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Budget Mode</label>
                  <div className="flex gap-3">
                    {[
                      { value: 'off', label: 'Off', icon: X },
                      { value: 'usd', label: 'USD', icon: DollarSign },
                      { value: 'tokens', label: 'Tokens', icon: Hash },
                    ].map(opt => {
                      const Icon = opt.icon;
                      return (
                        <button key={opt.value}
                          onClick={() => updateBudgetField('mode', opt.value)}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                            agentSettings.budget?.mode === opt.value
                              ? 'bg-indigo-600 text-white border-indigo-600'
                              : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                          }`}>
                          <Icon className="w-4 h-4" /> {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                {agentSettings.budget?.mode && agentSettings.budget.mode !== 'off' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Monthly Budget ({agentSettings.budget.mode === 'usd' ? 'USD' : 'Tokens'})
                    </label>
                    <input type="number" value={agentSettings.budget?.monthly_budget || 0}
                      onChange={(e) => updateBudgetField('monthly_budget', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                      min="0" step={agentSettings.budget.mode === 'usd' ? '0.01' : '1000'} />
                    {agentSettings.budget?.monthly_budget > 0 && (
                      <div className="flex gap-4 mt-2 text-xs text-gray-500">
                        <span>Daily: ~{(agentSettings.budget.monthly_budget / 30).toFixed(2)}</span>
                        <span>Weekly: ~{(agentSettings.budget.monthly_budget / 4.28).toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                )}
                {/* Usage bar */}
                <div className="pt-2 border-t border-gray-100">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm text-gray-600">This Month's Usage</p>
                    <p className="text-sm font-medium text-gray-900">
                      {(agentSettings.usage?.tokens_this_month || 0).toLocaleString()} tokens
                    </p>
                  </div>
                  <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                    {agentSettings.budget?.monthly_budget > 0 && agentSettings.budget?.mode === 'tokens' ? (
                      <div className="h-full bg-indigo-500 rounded-full" style={{
                        width: `${Math.min(100, ((agentSettings.usage?.tokens_this_month || 0) / agentSettings.budget.monthly_budget) * 100)}%`
                      }} />
                    ) : (
                      <div className="h-full bg-indigo-500 rounded-full" style={{ width: '0%' }} />
                    )}
                  </div>
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>Day {agentSettings.usage?.days_elapsed} of {agentSettings.usage?.days_in_month}</span>
                    <span>{agentSettings.usage?.days_remaining} days remaining</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Memory Stats */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Memory Stats</h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-gray-900">{agentSettings.memory_stats?.total || 0}</p>
                  <p className="text-xs text-gray-500">Total</p>
                </div>
                {['general', 'decision', 'preference', 'learning'].map(cat => (
                  <div key={cat} className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-gray-900">{agentSettings.memory_stats?.by_category?.[cat] || 0}</p>
                    <p className="text-xs text-gray-500 capitalize">{cat}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Save Button */}
            {settingsDirty && (
              <div className="flex justify-end">
                <button onClick={saveSettings}
                  className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 shadow-sm">
                  <Save className="w-4 h-4" /> Save Settings
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </AgentLayout>
  );
};

export default AdminTradosAgentSettings;
